import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { resources } from "@constants/hardcoded-strings";
import { LOGIN_URL, LOGOUT_URL } from "@constants/routes";
import { ModalContext } from "@context/use-modal-context";
// import { useGoogleAnalytics } from "@hooks/use-google-analytics";
import { useIsLoading } from "@hooks/use-is-loading";
import { ModalContextData, ModalContextState } from "@idw-types/context";
import { getSessionReduxRequest } from "@redux-requests/session";
import { incrementRequestCount, setSession } from "@redux/actions/session-actions";
import { NavigationMenu } from "../navigation-menu/navigation-menu";
import { ContentContainer } from "../content/content-container";
import { LoadingIndicator } from "../common/loading-indicator/loading-indicator";
import { LoginContainer } from "@containers/login/login-container";
import { LogoutContainer } from "../logout/logout-container";
import { AppAlertsListContainer } from "../app-alert-list/app-alert-list-container";
import { ModalContainer } from "../common/modals/modal-container/modal-container";
import styles from "./app-container.less";
import { getHostnameWithScheme } from "@utils/http-util";
import { ResetSessionEventObject, UpdateSessionEventObject } from "@idw-types/update-session-event-object";
import { RESET_SESSION_TIMEOUT_EVENT_NAME } from "@constants/session-constants";
import { getSessionState } from "@redux/selectors/root-selectors";

export const AppContainer = () => {
    const dispatch = useDispatch();
    const routerLocation = useLocation();
    const navigate = useNavigate();

    const [modalContextState, setModalContextState] = useState<ModalContextState>({
        modalType: undefined,
        renderFunction: undefined
    });

    const modalContext: ModalContextData = {
        modalState: modalContextState,
        setModalState: setModalContextState
    };

    const { isLoading: isLoadingSession, session } = useSelector(getSessionState);

    const [isNavigationMenuCollapsed, setIsNavigationMenuCollapsed] = useState(false);

    useEffect(() => {
        const updateSession = (event: CustomEvent<UpdateSessionEventObject>) => {
                dispatch(setSession(event.detail.session));
           
            if(event.detail?.errorCode) {
                const loginUrl = `${getHostnameWithScheme()}${LOGIN_URL}?errorCode=USER_NOT_FOUND`;
                window.location.href = loginUrl;
            }
        };

        const resetSessionTimeout = (event: CustomEvent<ResetSessionEventObject>) => {
            if(event.detail.shouldResetTimeout && session){
                dispatch(incrementRequestCount());
            }  
        };

        window.addEventListener("updateSessionEvent", updateSession);
        window.addEventListener(RESET_SESSION_TIMEOUT_EVENT_NAME, resetSessionTimeout);

        return () => {
            window.removeEventListener("updateSessionEvent", updateSession);
            window.removeEventListener(RESET_SESSION_TIMEOUT_EVENT_NAME, resetSessionTimeout);
        };
    }, [session]);

    useEffect(() => {
        if (!isLoadingSession && !session && routerLocation.pathname !== LOGIN_URL) {
            getSessionReduxRequest(dispatch)(routerLocation, navigate);
        }
    }, [isLoadingSession, session, routerLocation.pathname]);

    useIsLoading();
    // useGoogleAnalytics();

    const contentElement = (
        <>
            {!isLoadingSession && session && (
                <div className={styles.appContent}>
                    <NavigationMenu
                        session={session}
                        isNavigationMenuCollapsed={isNavigationMenuCollapsed}
                        setIsNavigationMenuCollapsed={setIsNavigationMenuCollapsed}
                    />
                    <ContentContainer
                        session={session}
                        isNavigationMenuCollapsed={isNavigationMenuCollapsed}
                    />
                </div>
            )}
            <div id="react-select-portal"></div>
            {isLoadingSession && (
                <div className={styles.appLoading}>
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                </div>
            )}
        </>
    );

    return (
        <div className={styles.appContainer}>
            <ModalContext.Provider value={modalContext}>
                <AppAlertsListContainer />
                <Routes>
                    <Route path={LOGOUT_URL} element={<LogoutContainer />} />
                    <Route path={LOGIN_URL} element={<LoginContainer />} />
                    <Route path={"*"} element={contentElement} />
                </Routes>
                <ModalContainer />
            </ModalContext.Provider>
        </div>
    );
};